<template>
    <features-template-base
    oneColumnWrapper
    :aboveAllApp="aboveAllApp"
    :nonAuthenticatedFeature="nonAuthenticatedFeature"
    :thirdLevelMenu="thirdLevelMenu"
    :hideClose="hideClose"
    :centerLogo="centerLogo"
    :fullWhiteBox="fullWhiteBox && !noWhiteBox"
    :customBackgroundImage="customBackgroundImage"
    :showFiltersAndEmptyState="showFiltersAndEmptyState"
    :customBreadcrumbs="customBreadcrumbs"
    :customHeaderTabs="customHeaderTabs"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="errorStateMessage"
    :tokenTitleAppend="tokenTitleAppend"
    :tokenTitleFull="tokenTitleFull"
    :tokenParams="tokenParams"
    :tokenCustomUseParams="tokenCustomUseParams"
    :showingFooter="footerButtonsParams"
    :showSearchSlot="showSearchSlot"
    :showTopRightAreaSlot="showTopRightAreaSlot"
    :hideHeaderButtons="hideHeaderButtons"
    @floatingButtonClicked="emit('floatingButtonClicked')"
    @challengeSuccessHandler="emit('challengeSuccessHandler', $event)"
    @challengeErrorHandler="emit('challengeErrorHandler', $event)"
    @mobileScrollValueUpdated="expandedFloatingButton = $event === 0"
    @scrollValueUpdated="expandedFloatingButton = $event === 0"
    @customTabClicked="emit('customTabClicked', $event)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Mobile top right button slot -->
        <template v-if="getMobileAppState && showTopRightButtonMobileSlot" #top-right-button-mobile-slot><slot name="top-right-button-mobile-slot"/></template>
        <!-- Slot for elements position on top area of header in web, and on top of content in mobile -->
		<template v-if="showTopRightAreaSlot" #accounts-card-slot-area><slot name="accounts-card-slot-area"/></template>
        <!-- Slot for search in navigation-header -->
        <template v-if="showSearchSlot" #top-search-area-slot><slot name="top-search-area-slot"/></template>
        <!--  -->
        <template v-if="showTabsExtraSlot" #tabs-slot-extra-content><slot name="tabs-slot-extra-content"/></template>
        <!-- Content -->
        <div
        v-if="!applyErrorState || showFiltersAndErrorState"
        ref="oneColumnWrapperRef"
        class="one-column-wrapper"
        :class="{
            'visible-none': hideWhiteBoxOnLoad,
            'show-filters-and-empty-state': showFiltersAndEmptyState,
            'no-white-box': noWhiteBox,
            'full-white-box': fullWhiteBox,
            'with-footer-visible': showFooter && footerButtonsParams,
            'mobile': getMobileAppState
        }">
            <slot></slot>
        </div>
        <!-- Empty-State-->
        <template #empty-state-slot><slot name="empty-state-slot"></slot></template>
        <!-- Floating Button -->
        <template #floating-button-area-slot>
            <!-- Floating Button -->
            <button-floating
            v-if="showFloatingButton && floatingButtonParams"
            :class="{'mobile': getMobileAppState}"
            :extended="expandedFloatingButton"
            :icon="floatingButtonParams.icon"
            :label="floatingButtonParams.label"
            @clicked="emit('floatingButtonClicked')"/>
        </template>

        <template #footer-area-slot>
            <!-- Feature Footer -->
            <navigation-footer
            v-if="footerButtonsParams"
            :buttonsParams="footerButtonsParams"
            :showFooter="showFooter"
            :footerWithInfoDisplayer="footerWithInfoDisplayer"
            :buttonFullWidth="buttonFullWidth">
                <slot name="footer-info-displayer"></slot>
            </navigation-footer>
        </template>
    </features-template-base>

</template>

<script setup>
import FeaturesTemplateBase from '@/components/wrappers/featuresTemplates/Base'
import ButtonFloating from '@/components/generic/buttons/Floating'
import NavigationFooter from '@/components/generic/navigation/Footer'
/* Vue */
import { ref, computed, useSlots, watch } from 'vue'
/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
const projectBaseStore = useProjectBaseStore()
const { getMobileAppState } = storeToRefs(projectBaseStore)
const { updateFooterShowState } = projectBaseStore

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'floatingButtonClicked', 'customTabClicked', 'challengeSuccessHandler', 'challengeErrorHandler'])

/* Props
@aboveAllApp {Boolean} prop to put this screen in front of the app (specific use cases)
@nonAuthenticatedFeature {Boolean} prop to apply specific styles for this type of features
@thirdLevelMenu {Boolean}
@hideClose {Boolean} prop to prevent close button from being shown in nonAuthenticatedFeatures or mobile
@centerLogo {Boolean} prop to center on nonAuthenticatedFeatures our logo on center
@hideWhiteBoxOnLoad {Boolean} - to remove white box and paddings during load
@showFiltersAndEmptyState {Boolean} - prop to appply correct sytles
@showFiltersAndErrorState {Boolean} - prop to appply correct sytles
@showTopRightAreaSlot {Boolean}
@showSearchSlot {Boolean}
@noWhiteBox {Boolean} - to remove the white box and paddings
@fullWhiteBox {Boolean} - to apply white box to all content and remove paddings
@customBackgroundImage {string}
@customBreadcrumbs {Object} - if our breadcrumbs are not direct from menu construction based on route, use this object
@customHeaderTabs {Array} - array with custom tabs
@hideHeaderButtons {Boolean}
@showLoader {Boolean} - prop to show loader
@showOverlayLoader {Boolean} - prop to apply overlay loader
@applyErrorState {Boolean} - prop to apply Error State
@errorStateMessage {String} - message to display on errorState
@showFloatingButton {Boolean} - prop to trigger if floating button should be showned or not
@floatingButtonParams {Object} object with info for floating button
@buttonsParams { Array } each item is an Object
    - buttonType { String } primary or tertiary
    - buttonPosition { String } left or right
    - label { String }
    - icon { String }
    - disabled { Boolean }
    - callbackFunction { Function }
@showFooter { Boolean }
@buttonFullWidth { Boolean }
@footerWithInfoDisplayer {Boolean} - to show an info displayer on the top of the footer
@buttonFullWidth {Boolean}
@tokenTitleAppend {String} - string to appentd at token modal title
@tokenTitleFull {String} - string to use as token modal title
@tokenParams {Object} - some times token submit will have additional params to send with token, when so, send it in this prop
@tokenCustomUseParams {Object} - when we want to have this modal but with apis diferent than the generic ones we send this object with:
    - messageToDisplay {String}
    - sendTokenAction {string} - 'moduleName/actionName'
    - resendTokenAction {string} - 'moduleName/actionName'
    - payload {Object} info that we want to send along with the response
    - resendPayload {Object} with info needed to resent token
    - forceResendChannelString {String} - ['email', 'sms'] - prop to ignore session token channel and use custom one
*/
const props = defineProps({
    aboveAllApp: Boolean,
    nonAuthenticatedFeature: Boolean,
    thirdLevelMenu: Boolean,
    hideClose: Boolean,
    centerLogo: Boolean,
    fullWhiteBox: Boolean,
    customBackgroundImage: String,
    hideWhiteBoxOnLoad: Boolean,
    showFiltersAndEmptyState: Boolean,
    showFiltersAndErrorState: Boolean,
    showTopRightAreaSlot: Boolean,
    showSearchSlot: Boolean,
    noWhiteBox: Boolean,
    customBreadcrumbs: Object,
    customHeaderTabs: Array,
    hideHeaderButtons: Boolean,
    /* Loader Props */
    showLoader: Boolean,
    showOverlayLoader: Boolean,
    /* ErrorState Props */
    applyErrorState: Boolean,
    errorStateMessage: String,
    /* Floating Button Props */
    showFloatingButton: Boolean,
    floatingButtonParams: Object,
    /* Footer Props */
    showFooter: Boolean,
    footerButtonsParams: Array,
    footerWithInfoDisplayer: Boolean,
    buttonFullWidth: Boolean,
    /* Token related Props */
    tokenTitleAppend: String,
    tokenTitleFull: String,
    tokenParams: Object,
    tokenCustomUseParams: Object
})

const expandedFloatingButton = ref(true)
const oneColumnWrapperRef = ref('')

watch(() => props.showFooter, (newVal, oldVal) => {
    updateFooterShowState(props.showFooter)
})

/*
* For overlay header on scroll,
* we need to make sure that our slots are not being populated, and if not, remove them with a v-if
*/
const showTopRightButtonMobileSlot = computed(() => {
    return useSlots()['top-right-button-mobile-slot']
})

const showTabsExtraSlot = computed(() => {
    return useSlots()['tabs-slot-extra-content']
})

updateFooterShowState(props.showFooter)
</script>

<style lang="scss" scoped>
.one-column-wrapper {
    width: 100%;
    max-width: 1168px;
    margin: 0 auto;
    z-index: 1;
    flex-shrink: 0;

    &:not(.no-white-box) {
        @include cardGeneral1;

        &.show-filters-and-empty-state {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    &.visible-none {
        opacity: 0;
        height: 0;
        padding: 0;
    }

    &:not(.mobile) {
        &.full-white-box {
            min-height: calc(100% - 24px);

            &.with-footer-visible {
                min-height: calc(100% - 80px - 24px);
            }
        }
    }

    &.mobile {
        &:not(.no-white-box) {
            padding: 16px 0;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &.full-white-box {
            padding: 16px;
        }

        &.visible-none {
            opacity: 0;
        }

        > :deep(*) {
            &:not(.prevent-auto-margins) {
                width: calc(100% - 32px);
                margin-left: auto;
                margin-right: auto;
            }
        }
    }
}

.button-floating {
    position: absolute;
    bottom: 24px;
    right: 26px;
    z-index: 1;

    &.mobile {
        bottom: 16px;
        right: 16px;
    }
}
</style>
